import DropdownSelect from "../../../components/DropdownSelectModal";
import { allowedFoundStatuses, foundReportStatus } from "../utils/const";
import {
  ActionWrapper,
  AddLostItemButton,
  CloseOutlinedIcon,
  DatePickerWrapper,
  EditIcon,
  FlexWrapper,
  HeaderWrapper,
  InfoText,
  InfoWrapper,
  Label,
  LostInfoText,
  PenIconWrapper,
  ReportedInfoText,
  SelectWrapper,
  StyledHeading,
} from "./styles";
import { ReactComponent as FileIcon } from "../../../assets/images/FileIcon.svg";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { fetchTicketsByStatus, updateLostItemDate, updateLostItemStatus } from "../../../redux/features/board/columnsAsyncThunk";
import { DatePicker, message, Select, Spin } from "antd";
import { convertIntoBoardTicket, updateTicketLocally, updateTicketStatusLocally } from "../../../redux/features/board/columnsSlice";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import SystemPayRoutes from "../../../utils/constants/Routes";
import { convertUtcToLocal } from "../../../utils/helpers/convertUtcToLocal";
import { FoundItemTypeOptions, FoundReport, FoundStatuses } from "../../../types/FoundItemTypes";
import DiscardFoundItemWithReason from "./DiscardFoundItemReason";
import { columnConfig, columnOrderConfig } from "../../Reports/Board/const";
import { ReportType } from "../../../redux/features/board/columnTypes";
import { selectUsers } from "../../../redux/features/user/userSlice";
import { getAllCaseManager } from "../../../redux/features/user/userAsyncThunk";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { updateFoundItemCaseManager, updateFoundItemStatus } from "../../../redux/features/foundReport/foundReportAsyncThunk";
import { updateCaseManager } from "../../../redux/features/report/reportSlice";
import { unAssignCaseMangerOptionValue } from "../../../utils/constants/filterOption";
import { updateItemCaseManager } from "../../../redux/features/foundReport/foundReportSlice";
import { RequestResults } from "../../../redux/common/RequestStatus";
import { PlusOutlined } from "@ant-design/icons";
import EditReportDetailsModal from "../../ReportDetails/Modal/EditReportDetailsModal";
import { Report } from "../../../types";
import RecoverFoundItemWithReason from "./RecoverFoundItemReason";
import { selectUserRole } from "../../../redux/features/auth/selectors";
import { checkHasPermission } from "../../../utils/helpers/checkHasPermission";
import ChangeStatusModal from "./ChangeStatusModal";

interface ReportDetailsHeaderProps {
  report: FoundReport | null;
  isLoading: boolean;
  versionData: FoundReport | null;
}

const ReportDetailsHeader: React.FC<ReportDetailsHeaderProps> = ({ report, isLoading, versionData }) => {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<any>(report?.status);
  const [selectedCaseManager, setSelectedCaseManager] = useState<any>(report?.caseManager?.id);
  const [updateCaseManger, setUpdateCaseManger] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const [selectedDate, setSelectedDate] = useState(dayjs(report?.foundDate));
  const [dateVisible, setDateVisible] = useState(false);
  const [dateUpdateLoading, setDateUpdateLoading] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [showDeleteReasonModal, setShowDeleteReasonModal] = useState(false);
  const [showRecoverReasonModal, setShowRecoverReasonModal] = useState(false);
  const [editReportDetailsModal, setEditReportDetailsModal] = useState(false);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<number | null>(null);
  const lostReportDetails: any = useMemo(() => {
    return {
      foundItemId: report?.id,
      foundItemNumber: report?.itemNumber,
      location: report?.location,
      category: report?.category,
      subCategory: report?.subCategory,
      documents: report?.documents,
      versionCount: report?.versionCount,
      isUrgent: report?.isUrgent,
      description: report?.description,
      user: {
        email: report?.passengerEmail,
        email2: report?.passengerEmail2,
        firstName: report?.passengerName,
        lastName: report?.passengerName2,
        phone: report?.passengerPhone,
        phone2: report?.passengerPhone2,
      },
      isCreate: true,
      airport: report?.airport,
      caseManager: report?.caseManager,
      caseManagerName: report?.caseManagerName,
    }
  }, [report]);

  const columns = useAppSelector((state) => state.columns.tickets);
  const { caseManagers, status } = useAppSelector(selectUsers);
  const userRole = useAppSelector(selectUserRole);

  const handleUpdate = async (value: any, option: any) => {
    if (report?.id && report?.status !== value) {
      if (value === FoundStatuses.Discarded) {
        setShowDeleteReasonModal(true);
        // Don't update selectedStatus yet - wait for modal confirmation
      } else if (value === FoundStatuses.Open && report?.status === FoundStatuses.Discarded) {
        setShowRecoverReasonModal(true);
        // Don't update selectedStatus yet - wait for modal confirmation
      } else {
        // Check if this is a forward flow (increasing status number)
        const isForwardFlow = value > (report?.status ?? 0);

        if (isForwardFlow) {
          // Show confirmation modal with email option for forward flow
          setPendingStatusChange(value);
          setShowChangeStatusModal(true);
        } else {
          // Backward flow - change status immediately without email
          handleStatusChangeWithoutModal(value, false);
        }
      }
    }
  };

  const handleStatusChangeWithoutModal = async (value: number, sendEmail: boolean) => {
    if (!report?.id) return;

    setLoading(true);
    setSelectedStatus(value);
    try {
      const sourceStatus = report?.status ?? 0;
      await dispatch(
        updateFoundItemStatus({
          id: report.id.toString(),
          status: value,
          sendEmail: sendEmail,
        })
      ).unwrap();
      message.success({
        content: (
          <>
            Status changed from {foundReportStatus?.map((status) => (status.value === selectedStatus ? status.label : ""))} to <b>{foundReportStatus?.map((status) => (status.value === value ? status.label : ""))}</b>
          </>
        ),
      });
      await dispatch(updateTicketStatusLocally({ ticket: convertIntoBoardTicket(report), sourceStatus, targetStatus: value }));
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmStatusChange = async (sendEmail: boolean) => {
    if (!report?.id || pendingStatusChange === null) return;

    setLoading(true);
    const value = pendingStatusChange;
    setSelectedStatus(value);
    try {
      const sourceStatus = report?.status ?? 0;
      await dispatch(
        updateFoundItemStatus({
          id: report.id.toString(),
          status: value,
          sendEmail: sendEmail,
        })
      ).unwrap();
      message.success({
        content: (
          <>
            Status changed from {foundReportStatus?.map((status) => (status.value === selectedStatus ? status.label : ""))} to <b>{foundReportStatus?.map((status) => (status.value === value ? status.label : ""))}</b>
          </>
        ),
      });
      await dispatch(updateTicketStatusLocally({ ticket: convertIntoBoardTicket(report), sourceStatus, targetStatus: value }));
      setShowChangeStatusModal(false);
      setPendingStatusChange(null);
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCaseManager = async (value: any) => {
    if (report?.id && report?.status < FoundStatuses.FinalMatch) {
      setLoading(true);
      const isUnAssigned = value === unAssignCaseMangerOptionValue;
      try {
        await dispatch(
          updateFoundItemCaseManager({
            id: report?.id?.toString(),
            caseManager: isUnAssigned ? null : value,
          })
        ).unwrap();
        const oldCaseManagerName = report?.caseManager?.id ? caseManagers?.find((user) => user?.id === report?.caseManager?.id)?.firstName + " " + caseManagers?.find((user) => user?.id === report?.caseManager?.id)?.lastName : "";
        const newCaseManagerName = isUnAssigned ? "" : caseManagers?.find((user) => user?.id === value)?.firstName + " " + caseManagers?.find((user) => user?.id === value)?.lastName;
        const newCaseManagerFirstName = isUnAssigned ? "" : caseManagers?.find((user) => user?.id === value)?.firstName;
        const newCaseManger = isUnAssigned ? undefined : caseManagers?.find((user) => user?.id === value);
        message.success({
          content: (
            <>
              {isUnAssigned ? <>
                Case Manager {oldCaseManagerName} unassigned
              </> : oldCaseManagerName ? (
                <>
                  Case Manager changed from {oldCaseManagerName} to <strong>{newCaseManagerName}</strong>
                </>
              ) : (
                <>
                  Report assigned to <strong>{newCaseManagerName}</strong>
                </>
              )}
            </>
          ),
        });
        await dispatch(updateTicketLocally({ ticket: convertIntoBoardTicket({ ...report, caseManagerName: newCaseManagerFirstName, caseManager: newCaseManger }) }));
        await dispatch(updateItemCaseManager({ caseManager: newCaseManger }));
        setSelectedCaseManager(isUnAssigned ? "" : value);
        setUpdateCaseManger("")
      } catch (error) {
        console.log({ error });
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (report?.status) {
      setSelectedStatus(report?.status);
    }
  }, [report?.status]);

  useEffect(() => {
    if (report?.foundDate) {
      setSelectedDate(dayjs(report.foundDate));
    }
  }, [report]);

  useEffect(() => {
    if (report?.caseManager?.id) {
      setSelectedCaseManager(report?.caseManager?.id);
    }
    else {
      setSelectedCaseManager("");
    }
  }, [report?.caseManager?.id]);

  // Dispatch API calls for each status in columnConfig
  const fetchAllTickets = async () => {
    await Promise.all(columnConfig.map(({ status, type }) => {
      const columnType = type ?? ReportType.Found;
      const order = columnOrderConfig[`${status}-${columnType}`];
      return dispatch(fetchTicketsByStatus({ status, offset: 0, limit: 50, filters: {}, type: columnType, order: order }))
    }));
  };

  useEffect(() => {
    if (Object.keys(columns).length === 0 && report?.id) {
      fetchAllTickets();
    }
  }, [columns, report?.id]);

  const handleIconClick = () => {
    setDateVisible(!dateVisible);
  };

  // Handle outside click to close DatePicker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside the DatePicker wrapper AND not on the DatePicker popup
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node) && dateVisible) {
        // Check if the click is on the DatePicker popup (which has class 'ant-picker-dropdown' or 'lost-date-picker')
        const target = event.target as HTMLElement;
        const isDatePickerPopup = target.closest('.ant-picker-dropdown') || target.closest('.lost-date-picker');

        // Only close if the click is not on the DatePicker popup
        if (!isDatePickerPopup) {
          setDateVisible(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dateVisible]);

  const handleDateChange = async (date: any) => {
    try {
      if (date && report?.id) {
        const lostDate = date?.toISOString();
        setDateUpdateLoading(true);
        setSelectedDate(date);
        setDateVisible(false);
        // await dispatch(
        //   updateLostItemDate({
        //     id: report?.id?.toString(),
        //     lostDate: lostDate,
        //   })
        // ).unwrap();
      }
    } catch (error) {
    } finally {
      setDateUpdateLoading(false);
      setDateVisible(false);
    }
  };

  useEffect(() => {
    dispatch(
      getAllCaseManager()
    );
  }, [dispatch]);

  const caseManagerUsers = useMemo(() => {
    if (caseManagers?.length > 0) {
      const users: { value: string; label: ReactNode; key: string }[] = caseManagers?.map((user) => ({
        value: user?.id,
        label: user?.firstName + " " + user?.lastName,
        key: user?.id
      }));

      if (report?.caseManager?.id) {
        // Add "Unassign User" option at the end
        users.push({
          value: unAssignCaseMangerOptionValue,
          label: <span style={{ color: "red" }}>Unassign User</span>,
          key: unAssignCaseMangerOptionValue
        });
      }

      return users;
    }
    return [];
  }, [caseManagers, report?.caseManager?.id]);

  const confirmUpdateCaseManager = useMemo(() => {
    if (caseManagerUsers?.length > 0) {
      const currentCaseManager = caseManagerUsers?.find((user) => user?.value === selectedCaseManager)?.label;
      const newCaseManager = caseManagerUsers?.find((user) => user?.value === updateCaseManger)?.label;
      return updateCaseManger && updateCaseManger !== unAssignCaseMangerOptionValue && currentCaseManager ? `Are you sure to unassign ${currentCaseManager} and assign to ${newCaseManager} for this report?` : updateCaseManger && updateCaseManger === unAssignCaseMangerOptionValue && currentCaseManager ? `Are you sure to unassign ${currentCaseManager}?` : updateCaseManger && !selectedCaseManager ? `Are you sure to assign ${newCaseManager} for this report?` : "";
    }
    return "";
  }, [selectedCaseManager, updateCaseManger, caseManagerUsers]);

  const confirmUpdateCaseManagerTitle = useMemo(() => {
    if (caseManagerUsers?.length > 0) {
      return updateCaseManger === unAssignCaseMangerOptionValue ? "Confirm Unassigning?" : "Confirm Assigning?";
    }
    return "";
  }, [selectedCaseManager, updateCaseManger, caseManagerUsers]);

  const isDiscardedStatus = useMemo(() => report?.status === FoundStatuses.Discarded, [report?.status])

  const hasLuggageItemType = useMemo(() => report?.foundType ? Number(report?.foundType) === FoundItemTypeOptions.FoundLuggage : false, [report?.foundType]);

  const filteredFoundReportStatus = useMemo(() => foundReportStatus.map((status) => ({
    ...status,
    disabled: !allowedFoundStatuses.includes(status.value) || !checkHasPermission(userRole, status.permissionKey),
  })), [userRole]);

  return (
    <>
      <HeaderWrapper>
        <FlexWrapper>
          <FileIcon />
          <StyledHeading>{report?.itemNumber}</StyledHeading>
          <InfoWrapper>
            <LostInfoText>
              <span>Found: </span>
              <span>{selectedDate ? convertUtcToLocal(selectedDate?.toString(), "MM/DD/YYYY") : ""}</span>
            </LostInfoText>
            {!isDiscardedStatus && <PenIconWrapper onClick={handleIconClick}>
              <EditIcon />
            </PenIconWrapper>}
            <Spin spinning={dateUpdateLoading}>
              <DatePickerWrapper ref={datePickerRef}>
                {dateVisible && (
                  <DatePicker
                    minDate={report?.foundDate ? dayjs(report?.foundDate) : undefined}
                    maxDate={report?.offloadDate ? dayjs(report?.offloadDate) : undefined}
                    open={dateVisible}
                    value={selectedDate}
                    onChange={handleDateChange}
                    popupClassName="lost-date-picker"
                  />
                )}
              </DatePickerWrapper>
            </Spin>
            <ReportedInfoText>
              <span>Reported: </span>
              <span>
                {report?.createdAt && convertUtcToLocal(report?.createdAt, "MM/DD/YYYY")} @ {report?.createdAt && convertUtcToLocal(report?.createdAt, "HH:mm:ss")}
              </span>
            </ReportedInfoText>
          </InfoWrapper>
        </FlexWrapper>
        <ActionWrapper>
          {hasLuggageItemType && <AddLostItemButton icon={<PlusOutlined />} type="primary" onClick={() => setEditReportDetailsModal(true)}>Add Lost Item</AddLostItemButton>}
          {editReportDetailsModal && report && <EditReportDetailsModal isModalOpen={editReportDetailsModal} handleOk={() => { setEditReportDetailsModal(false) }} handleCancel={() => { setEditReportDetailsModal(false) }} report={lostReportDetails} />}
          {!isDiscardedStatus && <SelectWrapper>
            <Label>Case Manager</Label>
            <Select style={{ minWidth: 150 }} placeholder="Select Case Manager" disabled={report?.status === FoundStatuses.FinalMatch} value={selectedCaseManager ? selectedCaseManager : undefined} options={caseManagerUsers} loading={loading || status === RequestResults.pending} onSelect={(value: any) => setUpdateCaseManger(value)} />
          </SelectWrapper>}
          <SelectWrapper>
            <Label>Status</Label>
            <Select style={{ minWidth: 150 }} value={selectedStatus} options={filteredFoundReportStatus} loading={loading} onSelect={handleUpdate} />
            <CloseOutlinedIcon onClick={() => navigate(SystemPayRoutes.reports)} />
          </SelectWrapper>
        </ActionWrapper>
        {showDeleteReasonModal && <DiscardFoundItemWithReason isOpen={showDeleteReasonModal} onClose={() => setShowDeleteReasonModal(false)} selectedRecord={report} />}
        {showRecoverReasonModal && <RecoverFoundItemWithReason isOpen={showRecoverReasonModal} onClose={() => setShowRecoverReasonModal(false)} selectedRecord={report} />}
        {showChangeStatusModal && pendingStatusChange !== null && (
          <ChangeStatusModal
            isOpen={showChangeStatusModal}
            onClose={() => {
              setShowChangeStatusModal(false);
              setPendingStatusChange(null);
            }}
            onConfirm={handleConfirmStatusChange}
            loading={loading}
            currentStatusLabel={foundReportStatus?.find((s) => s.value === report?.status)?.label || ""}
            newStatusLabel={foundReportStatus?.find((s) => s.value === pendingStatusChange)?.label || ""}
          />
        )}
        {updateCaseManger && <ConfirmationModal isModalOpen={updateCaseManger ? true : false} onCancel={() => setUpdateCaseManger("")} message={confirmUpdateCaseManager} title={confirmUpdateCaseManagerTitle} loading={loading} handleSave={() => handleUpdateCaseManager(updateCaseManger)} CancelButtonText="Cancel" confirmButtonText={updateCaseManger === unAssignCaseMangerOptionValue ? "Unassign" : "Assign"} />}
      </HeaderWrapper>
    </>
  );
};

export default ReportDetailsHeader;
